import { useState, useEffect, useMemo } from "react";
import { Select, MenuItem } from "@mui/material";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Sector,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import styles from "./Chart.module.css";

function Chart({ 
  chartType = "area", 
  data = [], 
  options = {}, 
  pieData = [], 
  dataByDropdown = {},
  defaultDropdownValue
}) {
  // Extract all options - no defaults, everything comes from props
  const {
    // Common options
    height,
    width,
    strokeColor,
    gradientId,
    showDropdown,
    dropdownOptions,
    title,
    showTitle,
    showHeader,
    containerClassName,
    wrapperClassName,
    
    // Donut chart
    innerRadius,
    outerRadius,
    chartWidth,
    chartMaxWidth,
    chartMaxHeight,
    legendWidth,
    chartLegendGap,
    pieChartMargin,
    hoverExpandAmount,
    pieDataKey,
    pieCx,
    pieCy,
    showLegend,
    legendItemFormatter,
    
    // Area chart
    areaChartMargin,
    gradientStops,
    areaDataKey,
    areaType,
    areaStrokeWidth,
    showAreaGradient,
    showAreaGrid,
    showAreaXAxis,
    showAreaYAxis,
    areaXAxisProps,
    areaYAxisProps,
    areaTooltipProps,
    
    // Line chart
    highlightIndex,
    highlightColor,
    lineChartMargin,
    lineDataKey,
    lineType,
    lineStrokeWidth,
    showLineGrid,
    showLineXAxis,
    showLineYAxis,
    lineXAxisProps,
    lineYAxisProps,
    lineTooltipProps,
    highlightDotRadius,
    highlightDotStrokeColor,
    highlightDotStrokeWidth,
    highlightLineDashArray,
    highlightLineStrokeWidth,
    
    // Bar chart
    barDataKey,
    barFill,
    barHighlightedFill,
    barRadius,
    showBarXAxis,
    showBarYAxis,
    barYAxisProps,
    barYAxisDomain,
    barYAxisTicks,
    barSize,
    barTooltipRows,
    barPlaceholderFill,
  } = options;

  // state for dropdown
  const [selectedDropdownValue, setSelectedDropdownValue] = useState(
    defaultDropdownValue || (dropdownOptions && dropdownOptions.length > 0 ? dropdownOptions[0].value : "")
  );

  // State for active segment on hover (for donut chart)
  const [activeIndex, setActiveIndex] = useState(null);
  const [, setHoveredIndex] = useState(null);
  
  // State for hovered bar in bar chart (no click/selected state)
  const [hoveredBarIndex, setHoveredBarIndex] = useState(null);

  // Get current data based on dropdown selection - memoized for performance
  const currentData = useMemo(() => {
    if ((chartType === "donut" || chartType === "bar") && dataByDropdown && Object.keys(dataByDropdown).length > 0) {
      return dataByDropdown[selectedDropdownValue] || pieData || data;
    }
    if (chartType === "donut") {
      return pieData.length > 0 ? pieData : data;
    }
    return data;
  }, [chartType, dataByDropdown, selectedDropdownValue, pieData, data]);

  // Transform data for charts - memoized
  const chartData = useMemo(() => {
    if (chartType === "area" || chartType === "line") {
      if (!Array.isArray(data)) return [];
      return data.map((value, index) => ({
        name: index,
        value: value,
      }));
    }
    if (chartType === "bar") {
      return currentData;
    }
    return data;
  }, [chartType, data, currentData]);

  // Create unique gradient ID - memoized
  const gradientIdValue = useMemo(() => {
    return gradientId || `gradient-${Math.random().toString(36).substr(2, 9)}`;
  }, [gradientId]);

  // Unique filter ID for donut segment hover shadow
  const segmentShadowFilterId = useMemo(
    () => `donut-segment-shadow-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  // Reset activeIndex and hoveredIndex when dropdown selection changes
  useEffect(() => {
    if (chartType === "donut") {
      setActiveIndex(null);
      setHoveredIndex(null);
    }
    if (chartType === "bar") {
      setHoveredBarIndex(null);
    }
  }, [selectedDropdownValue, chartType]);

  // Unique filter ID for bar hover shadow
  const barHoverShadowFilterId = useMemo(
    () => `bar-hover-shadow-${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  // Render area chart
  const renderAreaChart = () => {
    if (!height) return null;
    
    return (
      <div style={{ width: width || "100%", height: `${height}px` }}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={chartData} margin={areaChartMargin || {}}>
            {showAreaGradient !== false && gradientStops && (
              <defs>
                <linearGradient id={gradientIdValue} x1="0" y1="0" x2="0" y2="1">
                  {gradientStops.map((stop, index) => (
                    <stop
                      key={index}
                      offset={stop.offset}
                      stopColor={strokeColor || stop.stopColor}
                      stopOpacity={stop.opacity}
                    />
                  ))}
                </linearGradient>
              </defs>
            )}
            {showAreaGrid !== false && (
              <CartesianGrid strokeDasharray="3 3" stroke="transparent" />
            )}
            {showAreaXAxis !== false && (
              <XAxis 
                dataKey="name" 
                hide={areaXAxisProps?.hide !== false}
                {...(areaXAxisProps || {})}
              />
            )}
            {showAreaYAxis !== false && (
              <YAxis 
                hide={areaYAxisProps?.hide !== false}
                {...(areaYAxisProps || {})}
              />
            )}
            <Tooltip 
              content={() => null}
              {...(areaTooltipProps || {})}
            />
            <Area
              type={areaType || "monotone"}
              dataKey={areaDataKey || "value"}
              stroke={strokeColor}
              strokeWidth={areaStrokeWidth || 2}
              fill={showAreaGradient !== false ? `url(#${gradientIdValue})` : strokeColor}
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render line chart with highlighted point
  const renderLineChart = () => {
    if (!height) return null;
    const lineData = chartData.length > 0 ? chartData : [];
    const renderCustomDot = (props) => {
      const { cx, cy, index } = props;
      if (index !== highlightIndex || highlightIndex < 0) return null;
      const chartBottom = height - (lineChartMargin?.bottom || 0);
      const dotRadius = highlightDotRadius || 4;
      const dotStrokeColor = highlightDotStrokeColor || "#fff";
      const dotStrokeWidth = highlightDotStrokeWidth || 2;
      const lineDashArray = highlightLineDashArray || "3 3";
      const lineStrokeWidth = highlightLineStrokeWidth || 1;
      const hColor = highlightColor || strokeColor;
      return (
        <g key={`dot-${index}`}>
          <circle cx={cx} cy={cy} r={dotRadius} fill={hColor} stroke={dotStrokeColor} strokeWidth={dotStrokeWidth} />
          <line x1={cx} y1={cy} x2={cx} y2={chartBottom} stroke={hColor} strokeDasharray={lineDashArray} strokeWidth={lineStrokeWidth} />
        </g>
      );
    };
    return (
      <div style={{ width: width || "100%", height: `${height}px`, position: "relative" }}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={lineData} margin={lineChartMargin || {}}>
            {showLineGrid !== false && (
              <CartesianGrid strokeDasharray="3 3" stroke="transparent" />
            )}
            {showLineXAxis !== false && (
              <XAxis dataKey="name" hide={lineXAxisProps?.hide !== false} {...(lineXAxisProps || {})} />
            )}
            {showLineYAxis !== false && (
              <YAxis hide={lineYAxisProps?.hide !== false} {...(lineYAxisProps || {})} />
            )}
            <Tooltip content={() => null} {...(lineTooltipProps || {})} />
            <Line
              type={lineType || "monotone"}
              dataKey={lineDataKey || "value"}
              stroke={strokeColor}
              strokeWidth={lineStrokeWidth || 2}
              dot={false}
              activeDot={false}
            />
            {highlightIndex >= 0 && (
              <Line
                type={lineType || "monotone"}
                dataKey={lineDataKey || "value"}
                stroke="transparent"
                strokeWidth={0}
                dot={renderCustomDot}
                activeDot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render donut chart
  const renderDonutChart = () => {
    if (!height) return null;
    const total = currentData.reduce((sum, entry) => sum + (entry[pieDataKey || "value"] || 0), 0);
    const renderActiveShape = (props) => {
      const { cx, cy, innerRadius: ir, outerRadius: or, startAngle, endAngle, fill } = props;
      return (
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={ir}
          outerRadius={or + (hoverExpandAmount || 0)}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke="none"
          filter={`url(#${segmentShadowFilterId})`}
        />
      );
    };
    const hasMaxSize = chartMaxWidth != null && chartMaxHeight != null && outerRadius != null;
    const donutSize = hasMaxSize ? Math.ceil(2 * outerRadius + (hoverExpandAmount || 0) * 2 + 16) : null;
    const scale = hasMaxSize && donutSize > 0
      ? Math.min(chartMaxWidth / donutSize, chartMaxHeight / donutSize, 1)
      : 1;

    return (
      <div
        className={styles["donut-chart-container"]}
        style={{
          width: width || "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: chartLegendGap || "20px",
        }}
      >
        <div
          style={{
            width: hasMaxSize ? chartMaxWidth : (chartWidth || "100%"),
            height: hasMaxSize ? chartMaxHeight : height,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: donutSize ?? "100%",
              height: donutSize ?? height,
              ...(hasMaxSize && scale < 1
                ? {
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: `translate(-50%, -50%) scale(${scale})`,
                    transformOrigin: "center center",
                  }
                : {}),
            }}
          >
          <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={pieChartMargin || {}}>
            <defs>
              <filter id={segmentShadowFilterId} x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="4" stdDeviation="2" floodColor="rgba(0, 0, 0, 0.25)" floodOpacity="1" />
                <feDropShadow dx="0" dy="4" stdDeviation="2" floodColor="rgba(0, 0, 0, 0.25)" floodOpacity="1" />
              </filter>
            </defs>
            <Pie
              data={currentData}
              cx={pieCx || "50%"}
              cy={pieCy || "50%"}
              labelLine={false}
              label={false}
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              fill="#8884d8"
              dataKey={pieDataKey || "value"}
              stroke="none"
              activeIndex={activeIndex}
              activeShape={hoverExpandAmount !== undefined ? renderActiveShape : undefined}
              onMouseEnter={hoverExpandAmount !== undefined ? (_, index) => { setActiveIndex(index); setHoveredIndex(index); } : undefined}
              onMouseLeave={hoverExpandAmount !== undefined ? () => { setActiveIndex(null); setHoveredIndex(null); } : undefined}
            >
              {currentData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              wrapperStyle={{ border: "none", outline: "none", boxShadow: "none" }}
              contentStyle={{
                borderRadius: "4px",
                background: "rgba(255, 255, 255, 0.90)",
                height: "22px",
                padding: "6px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#000",
                textEdge: "cap",
                fontFamily: "Poppins",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "12px",
                border: "none",
                boxShadow: "none",
              }}
              itemStyle={{
                color: "#000",
                fontFamily: "Poppins",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "12px",
              }}
              labelStyle={{
                color: "#000",
                fontFamily: "Poppins",
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "12px",
              }}
            />
          </PieChart>
          </ResponsiveContainer>
          </div>
        </div>
        {showLegend !== false && (
          <div className={styles["donut-legend"]} style={{ width: legendWidth || "100%" }}>
            {currentData.map((entry, index) => {
              const value = entry[pieDataKey || "value"] || 0;
              const percentage = total > 0 ? ((value / total) * 100).toFixed(2) : 0;
              
              return (
                <div key={index} className={styles["legend-item"]}>
                  <div
                    className={styles["legend-color"]}
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className={styles["legend-text"]}>
                    {legendItemFormatter 
                      ? legendItemFormatter(entry, index, percentage)
                      : entry.name
                    }
                  </span>
                  <span className={styles["legend-percentage"]}>
                    {legendItemFormatter ? '' : `${percentage}%`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render bar chart
  const renderBarChart = () => {
    if (!height) return null;
    const barData = chartData.length > 0 ? chartData : [];
    const maxY = (barYAxisDomain && barYAxisDomain[1]) ?? 90;
    const barDataWithPlaceholder = barData.map((d) => ({
      ...d,
      _placeholder: Math.max(0, maxY - (Number(d.value) || 0)),
    }));
    const defaultBarFill = barFill ?? "rgba(79, 70, 229, 0.60)";
    const hoverBarFill = barHighlightedFill || "#4F46E5";
    const placeholderFill = barPlaceholderFill ?? "#F5F5FF";
    const renderBarShape = (props) => {
      const { payload, x, y, width, height: barHeight } = props;
      const barIndex = barData.findIndex(
        (item) => item === payload || (item.name === payload?.name && item.value === payload?.value)
      );
      const isHovered = barIndex === hoveredBarIndex;
      const fillColor = isHovered ? hoverBarFill : defaultBarFill;
      const radius = barRadius || [4, 4, 0, 0];
      const topRadius = Math.max(radius[0] || 0, radius[1] || 0);
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={barHeight}
          rx={topRadius}
          ry={topRadius}
          fill={fillColor}
          filter={isHovered ? `url(#${barHoverShadowFilterId})` : undefined}
          style={{ cursor: "pointer", outline: "none" }}
          onMouseEnter={() => barIndex >= 0 && setHoveredBarIndex(barIndex)}
          onMouseLeave={() => setHoveredBarIndex(null)}
          onFocus={(e) => e.target.blur()}
        />
      );
    };
    return (
      <div
        style={{
          width: width || "100%",
          height: height ? `${height}px` : "100%",
          position: "relative",
          border: "none",
          outline: "none",
          flex: 1,
          minHeight: 0,
        }}
        tabIndex={-1}
        onFocus={(e) => e.target.blur()}
      >
        <ResponsiveContainer width="100%" height={height || "100%"}>
          <BarChart data={barDataWithPlaceholder} barCategoryGap={30} responsive={true}>
            <defs>
              <filter id={barHoverShadowFilterId} x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="8.232" stdDeviation="5" floodColor="rgba(105, 118, 235, 0.39)" floodOpacity="1" />
              </filter>
            </defs>
            {showBarXAxis !== false && (
              <XAxis
                dataKey={barDataKey || "name"}
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={({ x, y, payload }) => (
                  <g transform={`translate(${x},${y})`}>
                    <text x={0} y={0} dy={16} textAnchor="middle" fill="rgba(128, 128, 128, 0.69)" fontSize={10.396} fontFamily="Lato" fontStyle="normal" fontWeight={400}>
                      {payload.value}
                    </text>
                  </g>
                )}
              />
            )}
            {showBarYAxis !== false && (
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#767E9C", fontSize: 12 }}
                domain={barYAxisDomain || [0, 90]}
                ticks={barYAxisTicks || [0, 10, 20, 30, 40, 50, 60, 70, 80, 90]}
                {...(barYAxisProps || {})}
              />
            )}
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: "none", border: "none", background: "transparent", boxShadow: "none" }}
              contentStyle={{ background: "transparent", border: "none", padding: 0 }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const data = payload[0].payload;
                const rows = barTooltipRows && barTooltipRows.length > 0
                  ? barTooltipRows.map(({ label, key }) => ({
                      label: typeof label === "function" ? label(data) : label,
                      value: data[key] != null ? data[key] : "",
                    }))
                  : [{ label: barDataKey || "Strategy", value: data.name }, { label: "Value", value: data.value }];
                return (
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      padding: "12px 16px",
                      minWidth: "180px",
                      border: "none",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    {rows.map((row, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "24px",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#000",
                          lineHeight: "normal",
                          marginBottom: i < rows.length - 1 ? "6px" : 0,
                        }}
                      >
                        <span style={{ flex: 1 }}>{row.label}</span>
                        <span style={{ flexShrink: 0 }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Bar
              dataKey="value"
              stackId="stack1"
              radius={[0, 0, 0, 0]}
              fill={defaultBarFill}
              barSize={barSize !== undefined ? barSize : 40}
              shape={renderBarShape}
              activeBar={false}
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-out"
            />
            <Bar
              dataKey="_placeholder"
              stackId="stack1"
              fill={placeholderFill}
              barSize={barSize !== undefined ? barSize : 40}
              radius={barRadius || [4, 4, 0, 0]}
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-out"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render chart based on type
  const renderChart = () => {
    switch (chartType) {
      case "area":
        return renderAreaChart();
      case "line":
        return renderLineChart();
      case "donut":
        return renderDonutChart();
      case "bar":
        return renderBarChart();
      default:
        return renderAreaChart();
    }
  };

  // Render dropdown if needed
  const renderDropdown = () => {
    if (!showDropdown || !dropdownOptions || dropdownOptions.length === 0) {
      return null;
    }

    return (
      <div className={styles["chart-dropdown-container"]}>
        <div className={styles["time-filter-container"]}>
          <Select
            value={selectedDropdownValue}
            onChange={(e) => setSelectedDropdownValue(e.target.value)}
            className={styles["time-filter-dropdown"]}
            variant="standard"
            IconComponent={() => (
              <GlobalIcon
                iconName="DropdownArrow"
                className={styles["time-filter-arrow"]}
              />
            )}
            displayEmpty
            disableUnderline
          >
            {dropdownOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    );
  };

  // Render title with icon if provided
  const renderTitle = () => {
    if (!title || showTitle === false) return null;
    return (
      <div className={styles["chart-title-container"]}>
        <div className={styles["chart-icon"]}>
          <GlobalIcon
            iconName="cardLabelIcon"
            className={styles["chart-icon-svg"]}
          />
        </div>
        <h2 className={styles["chart-title"]}>{title}</h2>
      </div>
    );
  };

  // Early return for donut charts with no data
  if (chartType === "donut") {
    if (!currentData || currentData.length === 0) {
      return null;
    }
    return (
      <div className={wrapperClassName || styles["chart-wrapper"]}>
        {showHeader !== false && (
          <div className={styles["chart-header"]}>
            {renderTitle()}
            {renderDropdown()}
          </div>
        )}
        <div className={`${containerClassName || styles["chart-container"]} ${styles["donut-chart"]}`}>
          {renderChart()}
        </div>
      </div>
    );
  }

  // Early return for bar charts with header
  if (chartType === "bar") {
    if (!currentData || currentData.length === 0) {
      return null;
    }
    return (
      <div className={wrapperClassName || styles["chart-wrapper"]}>
        {showHeader !== false && (
          <div className={styles["chart-header"]}>
            {renderTitle()}
            {renderDropdown()}
          </div>
        )}
        <div className={`${containerClassName || styles["chart-container"]} ${styles["bar-chart"]}`}>
          {renderChart()}
        </div>
      </div>
    );
  }

  // Early return for area/line charts with no data
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className={containerClassName || styles["chart-container"]}>
      {renderChart()}
    </div>
  );
}

export default Chart;
